import {
	A,
	all,
	ElementRenderer,
	Button,
	Checkbox,
	Div,
	Input,
	Label,
	Form,
	reactive,
	Search,
	Span,
	TD,
	TR,
} from 'alkali'
import './StudyList.scss'
import { selectedOutcomes, groupedInterventions } from 'model/EvidenceSearch'
import { whenInitialSearch, analysisType, selectedStudies, updateAnalysis, analysisSelections } from 'model/SearchQuery'
import { analysisResults } from 'model/Analysis'
import { getCIWidth } from 'model/AnalyzeStudy'
import noty from 'components/generic/noty'

export class ForestPlot extends Div{
	async ready(properties) {
		let results
		try {
			results = (await analysisResults)[0]
		} catch(error) {
			noty({
				text: error.toString(),
				layout: 'center',
				type: 'error',
			})
			return
		}
		if (!properties.grid.reportView)
			// if we already switched out of this view
			return
		let ciWidth = getCIWidth()
		let outcome = selectedOutcomes.valueOf()[0]
		
		let selectedCount = results.studyIds.length
		let scale = new Div('.master-scale', {})
		let LS = properties.grid.querySelectorAll('.dgrid-selected.analyzable.comparative-effect,.cohort-view .dgrid-selected.analyzable,.dgrid-selected.qualitative-analyzable')
		LS[LS.length-1]?.querySelector('.study-report')?.append(scale)

		let confidenceBarsWidthArr = []
		let maxScaleArr = []
		let minScaleArr = []
		let minScaleValText = []
		let maxScale
		let largest
		let smallestVal
		let smallest = {}
		let largestWidth

		for (let selectedId in properties.grid.selection) {
			if (!properties.grid.selection[selectedId]) continue
			let row = properties.grid.row(selectedId)
			let data = row.data
			if (!data)
				continue
			let index = results.studyIds.indexOf(selectedId + '')
			if (index == -1) {
				// check to see if it matches an alternate study
				for (let additionalStudy of data.additionalStudies || []) {
					index = results.studyIds.indexOf(additionalStudy.id + '')
					if (index > -1) {
						//data = additionalStudy // if we ever need to use data, make sure to update it
						break
					}
				}
				if (index == -1) {
					row.element.classList.add('no-results')
					continue
				}
			}
			row.element.reportAnalyzedEffect(results.yi[index], results.vi[index], results.scale)
			let position = results.yi[index] * results.scale
			let confidenceIntervalWidth = (Math.sqrt(results.vi[index]) * ciWidth * results.scale) + '%'
			let left = (position + 40 - parseFloat(confidenceIntervalWidth)) + '%'
			let width = (Math.sqrt(results.vi[index]) * ciWidth * results.scale) * 2
			let maxScaleTemp = parseFloat(left) + width
			maxScaleArr.push(maxScaleTemp)
			minScaleArr.push(parseFloat(left))
			minScaleValText.push(results.yi[index])
			confidenceBarsWidthArr.push(parseFloat(confidenceIntervalWidth))
		}
		//right most point of forest plot 
		largest = !analysisSelections.value.settings.reverseDirection ? maxScaleArr[0] : minScaleArr[0]
		//left most point of forest plot
		smallest = !analysisSelections.value.settings.reverseDirection ? minScaleArr[0] : maxScaleArr[0]

		smallestVal = minScaleValText[0]

		let smallestReverse  = maxScaleArr[0]

		largestWidth = confidenceBarsWidthArr[0]
		for (var i = 1; i < maxScaleArr.length; i++) {
			if (!analysisSelections.value.settings.reverseDirection) {
				if (maxScaleArr[i] > largest) {
					largest = maxScaleArr[i]
				}
				if (maxScaleArr[i] < smallestReverse) {
					smallestReverse = maxScaleArr[i]
				}
				if (confidenceBarsWidthArr[i] > largestWidth) {
					largestWidth = confidenceBarsWidthArr[i]
				}
				if (minScaleArr[i] < smallest) {
					smallest = minScaleArr[i]
				}
				if (minScaleValText[i] < smallestVal) {
					smallestVal = minScaleValText[i]
				}
			}
			else {
				if (maxScaleArr[i] < smallest) {
					smallest = maxScaleArr[i]
				}
				if (maxScaleArr[i] < smallestReverse) {
					smallestReverse = maxScaleArr[i]
				}
				if (confidenceBarsWidthArr[i] > largestWidth) {
					largestWidth = confidenceBarsWidthArr[i]
				}
				if (minScaleArr[i] > largest) {
					largest = minScaleArr[i]
				}
				if (minScaleValText[i] < smallestVal) {
					smallestVal = minScaleValText[i]
				}
			}
		}

		let isRatio
		const unscaleValue = value => {
			let effect = (value - 40 ) / results.scale
			if (outcome.type == 'Binary') {
				let measureBin = analysisSelections?.settings?.measureBin || 'RR'
				isRatio = measureBin != 'RD'
				effect = isRatio ? Math.exp(effect) : effect
				return (results.isCohort && measureBin == 'RR' || measureBin == 'RD') ? (effect * 100).toFixed(2) : effect.toFixed(2)
			} else {
				return effect
			}
		}
		const scaleValue = value => {
			if (outcome.type == 'Binary') {
				let measureBin = analysisSelections?.settings?.measureBin || 'RR'
				isRatio = measureBin != 'RD'
				value = isRatio ? Math.log(value) : value
				value = (results.isCohort && measureBin == 'RR' || measureBin == 'RD') ? value / 100 : value
			}
			return value * results.scale + 40
		}
		let xAxis
		const appendValues = (scaleAxis) => {
			if (scaleAxis > 1) {
				scale.innerHTML = ''
				xAxis.remove()
			}
				
			let axisVals = {}
			//right-most value	
			let maxVal = unscaleValue(largest * scaleAxis)
			let finalMaxVal = scaleValue(maxVal)
			let minScaleVal
			let minScaleVals
			//left-most value
			let finalSmallestMaxVal = smallest //scaleValue(smallestMax) // if smallestMax can have rounding errors that make it unusable
			let yVal = (isRatio ? 40 : results.scale + 40)
			
			let tick = new Div('.tick-value', {
				textContent: '|',
			})
			
			const appendTick = (element) => {
				setTimeout(() => {
					element.append(tick.cloneNode(true))
				}, 1000)
			}
			let maxScaleValTextContent = scaleAxis > 1 ? unscaleValue((((finalMaxVal * scaleAxis) - finalSmallestMaxVal) * 1) + finalSmallestMaxVal) : maxVal
			//right most element 
			let maxScaleVal = new Div('.scale-values', [Span('.scale-values-number', {textContent: this.parseNumber(maxScaleValTextContent)})], {
				attached() {
					this.style.left ='calc(' + ((finalMaxVal * scaleAxis)+ '% - ' + ((parseFloat(this.clientWidth) / 2) + 'px')) + ')'
					appendTick(this)
				}
			})
			scale.append(maxScaleVal)

			//position is calculated by setting the left attribute.
			//ensure that the position is representing the middle of the div (where the tick is) not the left
			let maxScaleValElement = properties.grid.querySelectorAll('.scale-values')[properties.grid.querySelectorAll('.scale-values').length-1]
			if (maxScaleValElement) {
				let smtest = (((finalMaxVal * scaleAxis) - finalSmallestMaxVal) * .25) + finalSmallestMaxVal
				let w  = (finalMaxVal + '%') + ' - ' + ((parseFloat(maxScaleValElement.clientWidth) / 2) + 'px')
				maxScaleValElement.style.left = 'calc(' + w + ')'
				axisVals[maxVal] = finalMaxVal
				//second largest element
				let test = (((finalMaxVal * scaleAxis) - finalSmallestMaxVal) * .75) + finalSmallestMaxVal
				let temp = unscaleValue(test)
				let leftSmallestMax = finalSmallestMaxVal + '%'
				axisVals[temp] = test
				//smallest element
				let innerTextSmallestMax = unscaleValue(finalSmallestMaxVal)
				minScaleVal = new Div('.scale-values', [Span('.scale-values-number', {textContent: this.parseNumber(unscaleValue(finalSmallestMaxVal))})], {
					ready() {
						appendTick(this)
					},
					//use attached() to find clientWidth
					attached() {
						w  = (leftSmallestMax) + ' - ' + ((parseFloat(this.clientWidth) / 2) + 'px')
						this.style.left = 'calc(' + w + ')'
					}
				}
				)
				scale.append(minScaleVal)
				
				axisVals[innerTextSmallestMax] = finalSmallestMaxVal
				//second smallest element
				let innerTextMinScaleVals = unscaleValue(smtest)
				minScaleVals = new Div('.scale-values', [Span('.scale-values-number', {textContent: this.parseNumber(unscaleValue(smtest))})], {
					ready() {
						appendTick(this)
					},
					attached() {
						w  = (smtest) + '% - ' + ((parseFloat(this.clientWidth) / 2) + 'px')
						this.style.left = 'calc(' + w + ')'
					}
				})
				scale.append(minScaleVals)
				//second largest element
				let ScaleVals = new Div('.scale-values', [Span('.scale-values-number', {textContent: this.parseNumber(unscaleValue(test))})], {
					ready() {
						appendTick(this)
					},
					attached() {
						w  = (test) + '% - ' + ((parseFloat(this.clientWidth) / 2) + 'px')
						this.style.left = 'calc(' + w + ')'
					}
				})
				scale.append(ScaleVals)
				
				xAxis = new Div('.x-axis', {
					left: finalSmallestMaxVal + '%',
					width: 0
				})
				let xAxisWidth = ((finalMaxVal * scaleAxis) - finalSmallestMaxVal) + .25 + '%'
				axisVals[innerTextMinScaleVals] = smtest
				//middle element
				let midtest = (((finalMaxVal * scaleAxis) - finalSmallestMaxVal) * .5) + finalSmallestMaxVal
				let innerTextMiddleScaleVal = unscaleValue(midtest)
				let middleScaleVal = new Div('.scale-values', [Span('.scale-values-number', {textContent: this.parseNumber(unscaleValue(midtest))} )], {
					ready() {
						appendTick(this)
					},
					attached() {
						w  = (midtest) + '% - ' + ((parseFloat(this.clientWidth) / 2) + 'px')
						this.style.left = 'calc(' + w + ')'
						setTimeout(() => {
							//wait until the first two axis values are attached then determin if values need to be scaled
							//use condition in this element since all others will be attached at this point
							if (minScaleVal.getBoundingClientRect().right > minScaleVals.getBoundingClientRect().left) {
								appendValues(1 + (100 - largest) / 300)
							}
						}, 1)
					}
				})
				scale.append(middleScaleVal)
				//set width after 100ms to trigger animation
				setTimeout(() => {
					xAxis.style.width = xAxisWidth
				}, 100)	
				LS[LS.length-1].querySelector('.study-report').append(xAxis)
				//find value closest to the left & right of yaxis. determin if 0 or 1 should be placed
				axisVals[innerTextMiddleScaleVal] = midtest
				let smallTemp = []
				let largeTemp = []
				for (const [key, value] of Object.entries(axisVals)) {
					if (parseFloat(value) < 40)
						smallTemp.push(40 - parseFloat(value))
					if (parseFloat(value) > 40)
						largeTemp.push(parseFloat(value) - 40)
				}
				const min = Math.min(...smallTemp)
				const max = Math.min(...largeTemp)
				let smallKey = Object.keys(axisVals).find(key => axisVals[key] === (40 - min))
				let bigKey = Object.keys(axisVals).find(key => axisVals[key] === (max - 40))
				let zeroOrOne = (yVal > 40 ? 0 : 1)
			}
		}
	appendValues(1)
		if (properties.grid.reportView) // check if we already switched out of this view
			properties.grid.classList.remove('transitioning-view')
	}
	
	parseNumber(scaleValue) {
		scaleValue = (scaleValue < .01 && scaleValue > 0 ? scaleValue.toString().substring(1,5) : scaleValue)
		scaleValue = (scaleValue < 1 && scaleValue > .01 ? scaleValue.toString().substring(1,4) : scaleValue.toString().substring(0,5))
		scaleValue = (scaleValue.toString().slice(-1) === '.' ? scaleValue.toString().substring(0 , scaleValue.toString().length-1) : scaleValue)
		scaleValue = (scaleValue < -10 && scaleValue.includes('.') ? scaleValue.toString().substring(0, 3) : scaleValue)
		scaleValue = (scaleValue > 10 && scaleValue.includes('.') ? scaleValue.toString().substring(0, scaleValue.indexOf('.')) : scaleValue)
		scaleValue = (scaleValue < 10 && scaleValue > 1 ? scaleValue.toString().substring(0, 4) : scaleValue)
		return scaleValue
	}

}

